import { useEffect, useMemo, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { isValid } from 'date-fns'

import {
  constants,
  dateTime as SharedDateTime,
  getInvoiceCustomFeeLines,
  getInvoiceCustomerMailingAddress,
  getInvoiceProcessingFee,
} from '@blocktickets/shared-utils'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'

import AuthService from '../../../utilities/services/auth.service'

import {
  formatCurrency,
  capitalizeString,
  formatTicketGroups,
  formatNumber,
  getPaymentMethodLabelForReceipt,
  isOrderPartiallyRefunded,
  getOrderRefundAmount,
  getOrderLastRefundDate,
  formatTicketLocationValue,
} from '../../../utilities/helpers'

import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'

import logo from '../../../assets/logo.svg'

import '../../../pages/InvoicePage/invoicePage.scss'
import './invoiceHtml.scss'

const { ORDER_STATUS } = constants

function resolveOrganizationPrimaryColor(...orgs) {
  for (const org of orgs) {
    if (!org) continue
    const branding = org.branding?.enabled === false ? null : org.branding
    const candidates = [
      branding?.primaryColor,
      org.primaryColor,
      org.brandColor,
    ]
    for (const candidate of candidates) {
      const color = String(candidate || '').trim()
      if (/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)) return color
    }
  }
  return ''
}

function unwrapAddress(address) {
  if (!address) return null
  if (Array.isArray(address)) return unwrapAddress(address[0])
  if (address.data) {
    const data = Array.isArray(address.data) ? address.data[0] : address.data
    return unwrapAddress(data?.attributes || data)
  }
  if (address.attributes && !address.address_1 && !address.city) {
    return unwrapAddress(address.attributes)
  }
  return address
}

function addressHasContent(address) {
  if (!address || typeof address !== 'object') return false
  return Boolean(
    address.address ||
    address.address_1 ||
    address.address1 ||
    address.city ||
    address.state ||
    address.zip ||
    address.zipcode
  )
}

function resolveInvoiceCurrency(...sources) {
  for (const source of sources) {
    const code = String(source || '')
      .trim()
      .toUpperCase()
    if (/^[A-Z]{3}$/.test(code)) return code
  }
  return 'USD'
}

function resolveOrganizationAddress(...addresses) {
  for (const address of addresses) {
    const unwrapped = unwrapAddress(address)
    if (addressHasContent(unwrapped)) return unwrapped
  }
  return null
}

function getOrganizationLogoUrl(image) {
  if (!image) return null

  const normalizeMediaUrl = (rawUrl) => {
    if (!rawUrl) return null

    const url = String(rawUrl).trim()
    if (!url) return null

    if (/^(data:|blob:|https?:)/i.test(url)) return url
    if (url.startsWith('//')) return `https:${url}`

    const normalizedPath = url.startsWith('/') ? url : `/${url}`
    if (!normalizedPath.startsWith('/uploads/')) return normalizedPath

    const apiBase = String(process.env.REACT_APP_API || '').trim()
    if (!apiBase) return normalizedPath

    const publicBase = apiBase.replace(/\/api\/?$/i, '')
    return `${publicBase}${normalizedPath}`
  }

  if (typeof image === 'string') return normalizeMediaUrl(image)

  return normalizeMediaUrl(
    image?.url ||
      image?.formats?.thumbnail?.url ||
      image?.formats?.small?.url ||
      image?.formats?.medium?.url ||
      image?.formats?.large?.url ||
      image?.data?.attributes?.formats?.thumbnail?.url ||
      image?.data?.attributes?.formats?.small?.url ||
      image?.data?.attributes?.formats?.medium?.url ||
      image?.data?.attributes?.formats?.large?.url ||
      image?.data?.attributes?.url ||
      image?.data?.url ||
      image?.attributes?.url ||
      null
  )
}

// Use date-fns-tz to show dates in the event's local timezone.
function formatInvoiceDate(dateLike, tz = 'UTC') {
  if (!dateLike) return 'N/A'

  let dateInput = dateLike
  if (typeof dateLike === 'string') {
    // format yyyy-MM-dd
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateLike)
    // format ISO strings
    const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(dateLike)
    if (isDateOnly) {
      // yyyy-MM-dd is not a real UTC moment
      // Anchor date-only strings at midnight UTC
      // convert to UTC to be midnight in tz
      // treat as wall‑time in tz
      dateInput = SharedDateTime.localToUtc(`${dateLike}T00:00:00`, tz)
    } else if (!hasZone) {
      // Treat zone-less timestamps as UTC
      dateInput = `${dateLike}Z`
    }
  }

  // turn the input into a Date object
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput)

  // formats date in the event timezone using SharedDateTime.formatInTimeZone(...) without changing the underlying moment in time - midnight time
  return isValid(date)
    ? SharedDateTime.formatInTimeZone(dateInput, tz, 'MMMM d, yyyy')
    : 'N/A'
}

// getDate normalizes dates for display.
// - For date-only strings (yyyy-MM-dd/ISO strings), keep them as-is and let formatting apply timezone.
// - For timestamps, return a Date object.
const getDate = (date) => {
  if (!date) return null
  if (date instanceof Date) return date
  if (typeof date === 'string') return date
  return new Date(date)
}

/**
 * Installment orders are paid across many charges. Their paidAt gets rewritten by
 * each Stripe installment sync, so it tracks the latest charge rather than when
 * this order was paid; datePaid holds the plan start date instead.
 */
function isPaymentPlanOrder(order) {
  return (
    String(order?.paymentType || '').toLowerCase() === 'payment-plan' ||
    Boolean(order?.payment_plan)
  )
}

function safeNumber(val, fallback = 0) {
  const n = Number(val)
  return Number.isFinite(n) ? n : fallback
}

function getCustomFeeNames(order) {
  const relation = order?.custom_fees
  if (!Array.isArray(relation)) return []
  return relation
    .map((fee) => String(fee?.name ?? fee?.attributes?.name ?? '').trim())
    .filter(Boolean)
}

function toCents(amount) {
  return Math.round(safeNumber(amount, 0) * 100)
}

function fromCents(cents) {
  return safeNumber(cents, 0) / 100
}

export default function InvoiceHtml({ order }) {
  const { closeModal } = useModal()
  const printRef = useRef(null)

  const authOrgs = AuthService.getOrg() || []
  const orderOrgUuid =
    order?.organizationUUID ||
    order?.organizationUuid ||
    order?.organization?.uuid ||
    order?.event?.organizationUUID ||
    order?.event?.organization?.uuid ||
    order?.package?.organizationUUID ||
    order?.package?.organization?.uuid ||
    order?.flex_pack?.organizationUUID ||
    order?.flex_pack?.organization?.uuid ||
    order?.access_pass_template?.organizationUUID ||
    order?.access_pass_template?.organization?.uuid
  const matchingAuthOrg = authOrgs.find(
    (orgItem) => orgItem?.uuid === orderOrgUuid
  )
  const authOrg = matchingAuthOrg || authOrgs?.[0]
  const orderOrg =
    order?.event?.organization ||
    order?.package?.organization ||
    order?.flex_pack?.organization ||
    order?.access_pass_template?.organization ||
    order?.organization
  const org = orderOrg || authOrg
  const sellerName = org?.name || 'blocktickets'
  const orgLogo =
    getOrganizationLogoUrl(orderOrg?.image) ||
    getOrganizationLogoUrl(authOrg?.image) ||
    logo
  const orgAddress = resolveOrganizationAddress(
    orderOrg?.address,
    (matchingAuthOrg || (!orderOrg ? authOrg : null))?.address
  )
  const accentColor = resolveOrganizationPrimaryColor(orderOrg, matchingAuthOrg)
  const supportEmail = [
    orderOrg?.email_support_email,
    matchingAuthOrg?.email_support_email,
    !orderOrg ? authOrg?.email_support_email : '',
  ]
    .map((value) => String(value || '').trim())
    .find(Boolean)
  const currencyCode = resolveInvoiceCurrency(
    orderOrg?.currency,
    matchingAuthOrg?.currency,
    !orderOrg ? authOrg?.currency : null,
    order?.currency
  )

  const selectedInvoice = order?.stripe_invoices?.[0]
  const payUrl =
    typeof selectedInvoice?.stripeInvoiceLink === 'string' &&
    /^https?:\/\//i.test(selectedInvoice.stripeInvoiceLink)
      ? selectedInvoice.stripeInvoiceLink
      : ''
  const openUrl = payUrl || selectedInvoice?.stripeInvoicePdf || null

  const event = order?.event
  const packageObj = order?.package
  const flexPackObj = order?.flex_pack
  const accessPassTemplateObj = order?.access_pass_template
  const eventVenue = event?.venue
  const packageVenue = packageObj?.venue
  const flexPackVenue = flexPackObj?.venue
  const accessPassVenue = accessPassTemplateObj?.venue
  const tz = SharedDateTime.ensureIanaTimeZone(
    eventVenue?.timezone ||
      packageVenue?.timezone ||
      flexPackVenue?.timezone ||
      accessPassVenue?.timezone
  )

  const eventName =
    packageObj?.name ||
    event?.name ||
    flexPackObj?.name ||
    accessPassTemplateObj?.name ||
    'Invoice'
  const issueDate = formatInvoiceDate(
    getDate(order?.dateOfIssue || order?.processedAt),
    tz
  )
  const dueDate = formatInvoiceDate(
    getDate(order?.dueDate || order?.processedAt),
    tz
  )

  // group tickets by originalTickets or package_tickets - to make formatTicketGroups work (to avoid repeating seats)
  const tickets = order?.package
    ? order?.package_tickets
    : order?.originalTickets || order?.tickets || []
  const flexPack = order?.flex_pack || null
  const accessPassTemplate = order?.access_pass_template || null
  const ticketLines = useMemo(() => formatTicketGroups(tickets), [tickets])
  let ticketQty = 0
  // if order is a package, use the original tickets (num of tickets for whole package) - tickets change depending on transfers
  if (order?.package) {
    ticketQty = order?.originalTickets?.length || order.tickets?.length || 0
  } else {
    // use the ticket lines (num of tickets for each line)
    ticketQty = ticketLines.reduce(
      (sum, l) => sum + safeNumber(l.ticketCount, 0),
      0
    )
  }
  const accessPassQty = Math.max(
    1,
    safeNumber(order?.accessPassQuantity, 0) ||
      (Array.isArray(order?.access_passes) ? order.access_passes.length : 0) ||
      1
  )

  const total = safeNumber(order?.total, 0)
  const serviceFee = safeNumber(order?.serviceFee, 0)
  // Prefer the customer-facing estimate, including the assignment pricing
  // snapshot used by historical POS orders whose stored estimate was zeroed.
  // Never use destinationAccountNet as invoice subtotal — that is org settlement, not customer pricing,
  // and leaves fee lines that do not add into total.
  const processingFee = getInvoiceProcessingFee(order)
  const totalFeeAmount = safeNumber(order?.totalFeeAmount, 0)
  const amountDue = safeNumber(order?.amountDue, 0)
  const amountOwed = amountDue || total
  const totalCents = toCents(total)
  const serviceFeeCents = toCents(serviceFee)
  const processingFeeCents = toCents(processingFee)
  const totalFeeAmountCents = toCents(totalFeeAmount)
  const subtotalCents =
    totalCents - serviceFeeCents - processingFeeCents - totalFeeAmountCents
  const subtotal = fromCents(subtotalCents)
  const isVoucherBoxOfficeOrder =
    Boolean(order?.voucher) &&
    Boolean(
      order?.box_office || order?.boxOffice || order?.source === 'box_office'
    )
  const displayTotal = fromCents(totalCents)
  const displayServiceFee = fromCents(serviceFeeCents)
  const displayAdditionalFee = fromCents(totalFeeAmountCents)
  const displayProcessingFee = fromCents(processingFeeCents)
  const customFeeLines = useMemo(() => getInvoiceCustomFeeLines(order), [order])
  const lines = useMemo(() => {
    const result = []
    const flexPackPrice = safeNumber(flexPack?.price, 0)
    const showFlexPackLine = Boolean(flexPack) && !isVoucherBoxOfficeOrder
    const showAccessPassLine = Boolean(accessPassTemplate)

    if (showFlexPackLine) {
      const gameTickets = safeNumber(flexPack?.gameTickets, 1)
      const description =
        gameTickets > 1
          ? `${flexPack?.name || 'Flex Package'} (includes ${gameTickets} game tickets)`
          : flexPack?.name || 'Flex Package'
      const unitPrice = gameTickets > 0 ? subtotal / gameTickets : 0
      result.push({
        description,
        qty: gameTickets,
        unitPrice: unitPrice,
        amount: unitPrice * safeNumber(gameTickets, 0),
      })
    }

    if (showAccessPassLine) {
      const unitPrice = accessPassQty > 0 ? subtotal / accessPassQty : 0
      result.push({
        description: accessPassTemplate?.name || 'Access Pass',
        qty: accessPassQty,
        unitPrice,
        // Use full subtotal so Amount matches Subtotal (avoids qty × unit drift).
        amount: subtotal,
      })
    }

    if (ticketLines.length > 0) {
      const ticketSubtotal = showFlexPackLine
        ? fromCents(toCents(subtotal - flexPackPrice))
        : subtotal
      const ticketUnitPrice = ticketQty > 0 ? ticketSubtotal / ticketQty : 0
      for (const line of ticketLines) {
        const lineQty = order?.package
          ? ticketQty
          : safeNumber(line.ticketCount, 0)
        // Package lines use the full ticket subtotal so Amount matches Subtotal
        // (avoids qty × rounded unit price drifting by a cent).
        const amt = order?.package ? ticketSubtotal : ticketUnitPrice * lineQty
        result.push({
          description: `${eventName} – Sec ${formatTicketLocationValue(line.sectionNumber, line.generalAdmission ? 'GA' : 'N/A')} – Row ${formatTicketLocationValue(line.rowNumber, line.generalAdmission ? 'GA' : 'N/A')} – Seat ${formatTicketLocationValue(line.seats, line.generalAdmission ? 'GA' : 'N/A')}`,
          qty: lineQty,
          unitPrice: ticketUnitPrice,
          amount: amt,
        })
      }
    }

    customFeeLines.forEach((line) => {
      result.push({
        description: line.name,
        qty: 1,
        unitPrice: line.amount,
        amount: line.amount,
        showPricing: true,
      })
    })

    return result
  }, [
    flexPack,
    accessPassTemplate,
    accessPassQty,
    isVoucherBoxOfficeOrder,
    ticketLines,
    ticketQty,
    subtotal,
    eventName,
    order?.package,
    customFeeLines,
  ])

  const [payQr, setPayQr] = useState('')
  useEffect(() => {
    if (!payUrl) {
      setPayQr('')
      return undefined
    }

    let cancelled = false
    QRCode.toDataURL(payUrl, {
      margin: 0,
      width: 180,
      color: { dark: '#1D2530', light: '#FFFFFF' },
    })
      .then((url) => {
        if (!cancelled) setPayQr(url)
      })
      .catch(() => {
        if (!cancelled) setPayQr('')
      })

    return () => {
      cancelled = true
    }
  }, [payUrl])

  // Each custom fee is its own line item. Fold those amounts into the subtotal
  // so they are not also listed as one combined additional fee.
  const customFeeLineCents = customFeeLines.reduce(
    (sum, line) => sum + toCents(line.amount),
    0
  )
  const displaySubtotal = customFeeLines.length
    ? fromCents(toCents(subtotal) + customFeeLineCents)
    : subtotal
  const preTaxSubtotal = fromCents(toCents(displaySubtotal / 1.13))
  const includedTax = fromCents(toCents(displaySubtotal - preTaxSubtotal))
  const additionalFeeNames = customFeeLines.length
    ? []
    : getCustomFeeNames(order)
  const additionalFeeLabel = additionalFeeNames.length
    ? `Additional fee – ${additionalFeeNames.join(', ')}`
    : 'Additional fee'

  const orderPaymentStatus = String(order?.paymentStatus || '').toLowerCase()
  const invoicePaymentStatus = String(
    selectedInvoice?.paymentStatus || ''
  ).toLowerCase()

  const isPaid =
    order?.status === ORDER_STATUS.COMPLETE ||
    order?.status === ORDER_STATUS.COMPLETE_FROM_EXCHANGE

  const isRefunded = order?.status === ORDER_STATUS.REFUNDED
  const totalRefunded = getOrderRefundAmount(order)
  const isPartiallyRefunded =
    !isRefunded && (isOrderPartiallyRefunded(order) || totalRefunded > 0)
  const partialRefundDate = formatInvoiceDate(
    getDate(getOrderLastRefundDate(order) || order?.updatedAt),
    tz
  )
  const originalAmountPaid = fromCents(totalCents + toCents(totalRefunded))

  // get date as Date in local timezone
  // paidAt is the actual payment timestamp; datePaid is a date-only field that
  // some order flows stamp at creation, so it can predate the real payment.
  const paidOnRaw = getDate(
    (isPaymentPlanOrder(order) ? null : order?.paidAt) ||
      order?.datePaid ||
      order?.paymentCompletedOn ||
      selectedInvoice?.paymentCompletedOn ||
      order?.invoiceCompleteOn ||
      order?.processedAt
  )

  const refundedDate = formatInvoiceDate(getDate(order?.updatedAt), tz)
  const paidOn = formatInvoiceDate(paidOnRaw, tz)

  const orderUser = order?.users_permissions_user
  const firstName = orderUser?.firstName || order?.firstName
  const lastName = orderUser?.lastName || order?.lastName
  const email = order?.email || orderUser?.email
  const mailingAddress = getInvoiceCustomerMailingAddress(order)
  const billToName =
    firstName && lastName ? capitalizeString(`${firstName} ${lastName}`) : 'N/A'

  const documentTitle = isRefunded
    ? 'Refund'
    : isPartiallyRefunded
      ? 'Receipt'
      : isPaid
        ? 'Receipt'
        : 'Invoice'
  const invoiceNumber =
    selectedInvoice?.stripeInvoiceId ||
    order?.invoiceUUID ||
    order?.orderId ||
    order?.id ||
    ''
  const amountHeadline = isRefunded
    ? `Refund processed on ${refundedDate}`
    : isPartiallyRefunded
      ? `Partial refund of ${formatCurrency(totalRefunded)} processed on ${partialRefundDate}`
      : isPaid
        ? `Marked as paid on ${paidOn}`
        : `${formatCurrency(amountOwed)} ${currencyCode} due ${dueDate}`
  const amountDueLabel = isRefunded
    ? 'Amount refunded'
    : isPartiallyRefunded
      ? 'Original amount paid'
      : isPaid
        ? 'Amount paid'
        : 'Amount due'
  const amountDueValue = formatCurrency(
    isRefunded || isPaid || isPartiallyRefunded
      ? isPartiallyRefunded
        ? originalAmountPaid
        : displayTotal
      : fromCents(toCents(amountOwed))
  )
  const pageFooterSummary = isRefunded
    ? `${invoiceNumber} · Refund processed on ${refundedDate}`
    : isPartiallyRefunded
      ? `${invoiceNumber} · Partial refund of ${formatCurrency(totalRefunded)}`
      : isPaid
        ? `${invoiceNumber} · Paid ${formatCurrency(displayTotal)} ${currencyCode} on ${paidOn}`
        : `${invoiceNumber} · ${formatCurrency(amountOwed)} ${currencyCode} due ${dueDate}`
  const venueName =
    eventVenue?.name ||
    packageVenue?.name ||
    flexPackVenue?.name ||
    accessPassVenue?.name ||
    ''
  const inPersonPayText = `By phone or in person: contact ${sellerName}${
    venueName ? ` at the ${venueName}` : ''
  }${org?.phoneNumber ? ` or call ${org.phoneNumber}` : ''}.`
  const showWaysToPay = !isPaid && !isRefunded && !isPartiallyRefunded

  const handleDownloadPdf = async () => {
    const contentEl = printRef.current
    if (!contentEl) return

    let qrSrc = payQr
    if (!qrSrc && payUrl) {
      try {
        qrSrc = await QRCode.toDataURL(payUrl, {
          margin: 0,
          width: 180,
          color: { dark: '#1D2530', light: '#FFFFFF' },
        })
      } catch (_) {
        qrSrc = ''
      }
    }

    const clone = contentEl.cloneNode(true)
    if (qrSrc) {
      const qrWrap = clone.querySelector('.invoice-doc__pay-qr')
      if (qrWrap) {
        let qrImg = qrWrap.querySelector('img')
        if (!qrImg) {
          qrImg = document.createElement('img')
          qrImg.alt = 'Scan to pay online'
          const placeholder = qrWrap.querySelector(
            '.invoice-doc__pay-qr-placeholder'
          )
          if (placeholder) placeholder.replaceWith(qrImg)
          else qrWrap.prepend(qrImg)
        }
        qrImg.setAttribute('src', qrSrc)
      }
    }

    const css = `
      html, body { height: 100%; }
      body { margin: 0; background: #fff; color: #1d2530; }
      @page { size: letter; margin: 0; }

      .invoice-doc {
        box-sizing: border-box;
        min-height: 11in;
        margin: 0;
        background: #ffffff;
        padding: 0 0.62in 0.38in;
        color: #1d2530;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
        display: flex;
        flex-direction: column;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .invoice-doc__accent {
        height: 10px;
        margin: 0 -0.62in 0.42in;
        background: var(--invoice-accent, #1d2530);
      }
      .invoice-doc__top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 24px;
        margin-bottom: 18px;
      }
      .invoice-doc__logo { height: 72px; width: auto; max-width: 180px; object-fit: contain; }
      .invoice-doc__title { font-size: 32px; font-weight: 650; letter-spacing: -0.02em; color: #1d2530; line-height: 1.1; }
      .invoice-doc__meta { display: grid; gap: 3px; margin-bottom: 36px; width: 340px; max-width: 100%; }
      .invoice-doc__meta-row { display: grid; grid-template-columns: 132px 1fr; gap: 16px; font-size: 13px; line-height: 1.35; }
      .invoice-doc__meta-label { font-weight: 700; color: #1d2530; }
      .invoice-doc__meta-value { font-weight: 400; color: #1d2530; text-align: left; }
      .invoice-doc__header {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 280px);
        gap: 32px;
        margin: 8px 0 42px;
        align-items: start;
      }
      .invoice-doc__seller, .invoice-doc__billto { min-width: 0; overflow-wrap: anywhere; word-break: break-word; }
      .invoice-doc__billto { text-align: left; }
      .invoice-doc__seller { text-align: right; }
      .invoice-doc__from-label, .invoice-doc__billto-label { font-size: 13px; margin-bottom: 8px; color: #1d2530; }
      .invoice-doc__seller-name, .invoice-doc__billto-name { font-size: 14px; font-weight: 700; margin-bottom: 2px; }
      .invoice-doc__seller-phone { margin-top: 0; }
      .invoice-doc__billto-missing { margin-top: 6px; font-size: 13px; font-weight: 600; color: #9a3412; }
      .invoice-doc__amount-due { margin: 0 0 28px; }
      .invoice-doc__amount-line { font-size: 22px; font-weight: 700; line-height: 1.3; color: #1d2530; }
      .invoice-doc__table { border: 0; border-radius: 0; overflow: visible; }
      .invoice-doc__thead, .invoice-doc__trow {
        display: grid;
        grid-template-columns: 1fr 64px 110px 110px;
        gap: 12px;
        padding: 8px 0;
        background: transparent;
        font-size: 13px;
        font-weight: 500;
        color: #1d2530;
      }
      .invoice-doc__thead { color: #606775; font-weight: 600; border-bottom: 1px solid #d5d8de; padding-bottom: 8px; }
      .invoice-doc__trow { border-top: 0; border-bottom: 1px solid #e6e8ee; }
      .invoice-doc__trow--empty { color: #606775; }
      .invoice-doc__desc-title { font-size: 13px; font-weight: 500; color: #1d2530; }
      .invoice-doc__num { text-align: right; font-variant-numeric: tabular-nums; }
      .invoice-doc__bottom { display: flex; justify-content: flex-end; margin-top: 0; }
      .invoice-doc__totals { width: 280px; display: grid; gap: 0; font-size: 13px; font-weight: 500; }
      .invoice-doc__totals-row { display: grid; grid-template-columns: 1fr auto; gap: 24px; padding: 8px 0; border-bottom: 1px solid #e6e8ee; }
      .invoice-doc__totals-row--strong { font-weight: 700; font-size: 13px; }
      .invoice-doc__totals-row--amount-due { border-bottom: 1px solid #e6e8ee; }
      .invoice-doc__totals-value { text-align: right; font-variant-numeric: tabular-nums; }
      .invoice-doc__pay { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; margin-top: 48px; break-inside: avoid; page-break-inside: avoid; }
      .invoice-doc__pay-title { color: var(--invoice-accent, #1d2530); font-weight: 700; font-size: 14px; margin-bottom: 8px; }
      .invoice-doc__pay-list { margin: 0; padding-left: 18px; font-size: 13px; line-height: 1.45; }
      .invoice-doc__pay-list li + li { margin-top: 6px; }
      .invoice-doc__pay-qr { width: 108px; text-align: center; }
      .invoice-doc__pay-qr img { width: 96px; height: 96px; display: block; margin: 0 auto; }
      .invoice-doc__pay-caption { margin-top: 6px; color: var(--invoice-accent, #1d2530); font-size: 12px; font-weight: 600; }
      .invoice-doc__notes { margin-top: 28px; font-size: 11px; line-height: 1.45; color: #606775; }
      .invoice-doc__page-footer {
        margin-top: auto;
        padding-top: 10px;
        border-top: 1px solid #e6e8ee;
        display: flex;
        justify-content: space-between;
        gap: 16px;
        font-size: 11px;
        color: #606775;
      }
      .invoice-doc__muted { font-weight: 400; font-size: 13px; color: #1d2530; }
      .invoice-doc__muted--note { margin-top: 8px; color: #606775; font-size: 12px; }
    `

    // Print via a hidden iframe (no new tab).
    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <base href="${window.location.origin}/" />
    <title>Invoice</title>
    <style>${css}</style>
  </head>
  <body>
    ${clone.outerHTML}
  </body>
</html>`

    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    iframe.style.opacity = '0'
    iframe.setAttribute('aria-hidden', 'true')

    const cleanup = () => {
      try {
        iframe.parentNode?.removeChild(iframe)
      } catch (_) {}
    }

    // Guard against multiple load events triggering multiple prints.
    let didTriggerPrint = false

    const tryTriggerPrint = () => {
      if (didTriggerPrint) return
      const doc = iframe.contentDocument
      if (!doc) return
      // Only print once the invoice content is actually present.
      const hasInvoice = doc.querySelector('.invoice-doc')
      if (!hasInvoice) return

      didTriggerPrint = true

      // Give layout/images a moment to resolve, then print.
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus()
        } catch (_) {}
        try {
          iframe.contentWindow?.print()
        } catch (_) {}

        // Clean up after print dialog opens/closes.
        // (onafterprint is not always reliable across browsers/iframes)
        setTimeout(cleanup, 5_000)
      }, 250)
    }

    iframe.addEventListener('load', () => {
      tryTriggerPrint()
      // Some browsers fire load before srcdoc is fully parsed; retry briefly.
      let retries = 0
      const interval = setInterval(() => {
        retries += 1
        tryTriggerPrint()
        if (didTriggerPrint || retries >= 20) {
          clearInterval(interval)
        }
      }, 50)
    })

    // Prefer srcdoc; set it before appending to reduce extra load events.
    let usedSrcDoc = false
    try {
      iframe.srcdoc = html
      usedSrcDoc = true
    } catch (_) {}

    document.body.appendChild(iframe)

    if (!usedSrcDoc) {
      try {
        const doc = iframe.contentWindow?.document
        if (!doc) return
        doc.open()
        doc.write(html)
        doc.close()
      } catch (_) {
        cleanup()
      }
    }
  }

  return (
    <>
      <Modal.Body className="pt-3">
        <div className="invoice-html-modal">
          <div
            className="invoice-doc"
            role="document"
            aria-label="Invoice"
            ref={printRef}
            style={
              accentColor ? { '--invoice-accent': accentColor } : undefined
            }
          >
            <div className="invoice-doc__accent" />
            <div className="invoice-doc__top">
              <div className="invoice-doc__title">{documentTitle}</div>
              <img
                className="invoice-doc__logo"
                src={orgLogo}
                alt={sellerName}
              />
            </div>

            <div className="invoice-doc__meta">
              <div className="invoice-doc__meta-row">
                <div className="invoice-doc__meta-label">Invoice number</div>
                <div className="invoice-doc__meta-value">{invoiceNumber}</div>
              </div>
              {(isPaid || isRefunded || isPartiallyRefunded) && (
                <div className="invoice-doc__meta-row">
                  <div className="invoice-doc__meta-label">Payment method</div>
                  <div className="invoice-doc__meta-value">
                    {getPaymentMethodLabelForReceipt(order)}
                  </div>
                </div>
              )}
              {isPartiallyRefunded && (
                <div className="invoice-doc__meta-row">
                  <div className="invoice-doc__meta-label">
                    Refunded to date
                  </div>
                  <div className="invoice-doc__meta-value">
                    {formatCurrency(totalRefunded)}
                  </div>
                </div>
              )}
              {!isRefunded ? (
                <>
                  <div className="invoice-doc__meta-row">
                    <div className="invoice-doc__meta-label">Date of issue</div>
                    <div className="invoice-doc__meta-value">{issueDate}</div>
                  </div>
                  <div className="invoice-doc__meta-row">
                    <div className="invoice-doc__meta-label">Date due</div>
                    <div className="invoice-doc__meta-value">{dueDate}</div>
                  </div>
                </>
              ) : (
                <div className="invoice-doc__meta-row">
                  <div className="invoice-doc__meta-label">Date paid</div>
                  <div className="invoice-doc__meta-value">{paidOn}</div>
                </div>
              )}
            </div>

            <div className="invoice-doc__header">
              <div className="invoice-doc__billto">
                <div className="invoice-doc__muted invoice-doc__billto-label">
                  {isRefunded ? 'Refunded to' : 'Bill to'}
                </div>
                <div className="invoice-doc__billto-name">{billToName}</div>
                {mailingAddress.lines.map((line, index) => (
                  <div
                    className="invoice-doc__muted invoice-doc__billto-line"
                    key={`${line}-${index}`}
                  >
                    {line}
                  </div>
                ))}
                {!mailingAddress.complete && (
                  <div className="invoice-doc__billto-missing">
                    {mailingAddress.lines.length
                      ? 'Mailing address incomplete'
                      : 'Mailing address unavailable'}
                  </div>
                )}
                <div className="invoice-doc__muted">{email || 'N/A'}</div>
              </div>
              <div className="invoice-doc__seller">
                <div className="invoice-doc__from-label">From</div>
                <div className="invoice-doc__seller-name">{sellerName}</div>
                {(orgAddress?.address ||
                  orgAddress?.address_1 ||
                  orgAddress?.address1) && (
                  <div className="invoice-doc__muted">
                    {orgAddress.address ||
                      orgAddress.address_1 ||
                      orgAddress.address1}
                  </div>
                )}
                {orgAddress?.address_2 && (
                  <div className="invoice-doc__muted">
                    {orgAddress.address_2}
                  </div>
                )}
                {(orgAddress?.city || orgAddress?.state) && (
                  <div className="invoice-doc__muted">
                    {[
                      orgAddress.city,
                      orgAddress.state,
                      orgAddress.zip || orgAddress.zipcode,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  </div>
                )}
                {org?.phoneNumber && (
                  <div className="invoice-doc__muted invoice-doc__seller-phone">
                    {org.phoneNumber}
                  </div>
                )}
                {org?.email && (
                  <div className="invoice-doc__muted">{org.email}</div>
                )}
              </div>
            </div>

            <div className="invoice-doc__amount-due">
              <div className="invoice-doc__amount-line">{amountHeadline}</div>
              {isRefunded && (
                <div className="invoice-doc__muted invoice-doc__muted--note">
                  The refund will be credited back to your original payment
                  method within 5-10 business days.
                </div>
              )}
              {isPartiallyRefunded && (
                <div className="invoice-doc__muted invoice-doc__muted--note">
                  A partial refund of {formatCurrency(totalRefunded)} has been
                  issued to the original payment method. The remaining order
                  balance is {formatCurrency(displayTotal)}.
                </div>
              )}
            </div>

            <div className="invoice-doc__table">
              <div className="invoice-doc__thead">
                <div>Description</div>
                <div className="invoice-doc__num">Qty</div>
                <div className="invoice-doc__num">Unit price</div>
                <div className="invoice-doc__num">Amount</div>
              </div>
              {lines.length === 0 ? (
                <div className="invoice-doc__trow invoice-doc__trow--empty">
                  <div className="invoice-doc__muted">
                    No line items found for this order.
                  </div>
                  <div />
                  <div />
                  <div />
                </div>
              ) : (
                lines.map((line, idx) => (
                  <div className="invoice-doc__trow" key={`${idx}`}>
                    <div className="invoice-doc__desc">
                      <div className="invoice-doc__desc-title">
                        {line.description}
                      </div>
                    </div>
                    {!order?.package || idx === 0 || line.showPricing ? (
                      <>
                        <div className="invoice-doc__num">
                          {formatNumber(line.qty)}
                        </div>
                        <div className="invoice-doc__num">
                          {formatCurrency(line.unitPrice)}
                        </div>
                        <div className="invoice-doc__num">
                          {formatCurrency(line.amount)}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="invoice-doc__num" />
                        <div className="invoice-doc__num" />
                        <div className="invoice-doc__num" />
                      </>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="invoice-doc__bottom">
              <div />
              <div className="invoice-doc__totals">
                <div className="invoice-doc__totals-row">
                  <div className="invoice-doc__totals-label">
                    <div>Subtotal</div>
                    {org?.display_tin && (
                      <div className="invoice-doc__totals-subtext">
                        includes {formatCurrency(includedTax)} HST (13%)
                      </div>
                    )}
                  </div>
                  <div className="invoice-doc__totals-value">
                    {formatCurrency(displaySubtotal)}
                  </div>
                </div>
                {displayServiceFee > 0 && (
                  <div className="invoice-doc__totals-row">
                    <div className="invoice-doc__totals-label">
                      Service charge
                    </div>
                    <div className="invoice-doc__totals-value">
                      {formatCurrency(displayServiceFee)}
                    </div>
                  </div>
                )}
                {displayAdditionalFee > 0 && customFeeLines.length === 0 && (
                  <div className="invoice-doc__totals-row">
                    <div className="invoice-doc__totals-label">
                      {additionalFeeLabel}
                    </div>
                    <div className="invoice-doc__totals-value">
                      {formatCurrency(displayAdditionalFee)}
                    </div>
                  </div>
                )}
                {displayProcessingFee > 0 && (
                  <div className="invoice-doc__totals-row">
                    <div className="invoice-doc__totals-label">
                      Processing fee
                    </div>
                    <div className="invoice-doc__totals-value">
                      {formatCurrency(displayProcessingFee)}
                    </div>
                  </div>
                )}
                <div className="invoice-doc__totals-row">
                  <div className="invoice-doc__totals-label">Total</div>
                  <div className="invoice-doc__totals-value">
                    {formatCurrency(
                      isPartiallyRefunded ? originalAmountPaid : displayTotal
                    )}
                  </div>
                </div>
                {isPartiallyRefunded && (
                  <>
                    <div className="invoice-doc__totals-row">
                      <div className="invoice-doc__totals-label">
                        Refunded to date
                      </div>
                      <div className="invoice-doc__totals-value">
                        -{formatCurrency(totalRefunded)}
                      </div>
                    </div>
                    <div className="invoice-doc__totals-row">
                      <div className="invoice-doc__totals-label">
                        Remaining balance
                      </div>
                      <div className="invoice-doc__totals-value">
                        {formatCurrency(displayTotal)}
                      </div>
                    </div>
                  </>
                )}
                <div className="invoice-doc__totals-row invoice-doc__totals-row--strong invoice-doc__totals-row--amount-due">
                  <div className="invoice-doc__totals-label">
                    {amountDueLabel}
                  </div>
                  <div className="invoice-doc__totals-value">
                    {amountDueValue} {currencyCode}
                  </div>
                </div>
              </div>
            </div>

            {showWaysToPay && (
              <div className="invoice-doc__pay">
                <div>
                  <div className="invoice-doc__pay-title">Ways to pay</div>
                  <ul className="invoice-doc__pay-list">
                    {payUrl && (
                      <li>
                        Online: scan the QR code to the right with your phone
                        camera to pay securely by card.
                      </li>
                    )}
                    <li>{inPersonPayText}</li>
                  </ul>
                </div>
                {payUrl && (
                  <div className="invoice-doc__pay-qr">
                    {payQr ? (
                      <img src={payQr} alt="Scan to pay online" />
                    ) : (
                      <div className="invoice-doc__pay-qr-placeholder" />
                    )}
                    <div className="invoice-doc__pay-caption">
                      Scan to pay online
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="invoice-doc__notes">
              {org?.taxId ? <div>HST - {org.taxId}</div> : null}
              {org?.tiname && org?.tincode ? (
                <div>
                  {org.tiname}: {org.tincode}
                </div>
              ) : null}
              <div>
                Please note our rules and regulations: all sales are final, and
                we do not offer exchanges or refunds.
              </div>
              {supportEmail ? (
                <div>For inquiries, email us at {supportEmail}.</div>
              ) : null}
            </div>

            <div className="invoice-doc__page-footer">
              <div>{pageFooterSummary}</div>
              <div>Page 1 of 1</div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Stack direction="horizontal" gap={2} className="ms-auto">
          <Button variant="outline-light" onClick={handleDownloadPdf}>
            Download PDF
          </Button>
          {openUrl && (
            <Button
              as="a"
              href={openUrl}
              target="_blank"
              rel="noreferrer"
              variant="outline-light"
            >
              Open invoice
            </Button>
          )}
          <Button variant="primary" onClick={closeModal}>
            Close
          </Button>
        </Stack>
      </Modal.Footer>
    </>
  )
}
